import { blinkConstants } from '@thoughtspot/blink-constants';
import {
    isDisableRedirectionLinksInNewTabEnabled,
    isLinkOverridesV2Enabled,
} from '@thoughtspot/embed-util';
import { BLINK_FLAGS, envFlags } from '@thoughtspot/env-flags';
import {
    FEATURE_FLAG_NAMES,
    isFeatureEnabled,
} from '@thoughtspot/feature-service';
import { jsonConstants } from '@thoughtspot/json-constants';
import {
    addQueryParamsToUrlTemplate,
    cleanupQueryParamsTemplateFromUrl,
    QueryParams,
} from '@thoughtspot/mfe-utilities';
import {
    getCurrentOrgEmbedUrls,
    getCurrentOrgId,
    isOrgUiEnabled,
} from '@thoughtspot/session-service';
import { isPublicUserEnabled } from '@thoughtspot/system-config-service';
import _ from 'lodash';
import { EmbedUrls, ListType } from '/@services/generated/graphql-types';
import { createWindowOpenInterceptor } from '/@utils/browser-interceptors';
import {
    EmbedUrlsEnum,
    EventType,
    setEmbedInitDataValues,
    subscribeToCustomCssEvents,
} from '/@utils/embed.util';
import { dispatchNonMemoEvent } from '/@utils/event-bridge/event-bridge';

export const EverywherePropKeys = {
    visualSDKComponent: 'VisualSDKComponent',
    visualSDKDataSourceGUIDs: 'VisualSDKDataSourceGUIDs',
    hostAppURL: 'HostAppURL',
    onPublicInstance: 'OnPublicInstance',
};

/**
 * Checks if a given URL belongs to the current cluster/origin
 *
 * @param href - The URL to check (can be relative or absolute)
 * @returns true if the URL is from the current origin, false if it's external
 *
 * @example
 * // Current origin: https://example.com
 * isCurrentClusterUrl('/pinboard/123')  // true (relative URL)
 * isCurrentClusterUrl('https://example.com/answer/abc')  // true (same origin)
 * isCurrentClusterUrl('https://external.com/page')  // false (different origin)
 */
export const isCurrentClusterUrl = (href: string): boolean => {
    try {
        const url = new URL(href, window.location.origin);
        return url.origin === window.location.origin;
    } catch (error) {
        dispatchNonMemoEvent(EventType.Error, {
            error: `Failed to parse URL in isCurrentClusterUrl: ${href}`,
            errorDetails:
                error instanceof Error ? error.message : String(error),
        });

        // If URL parsing fails, assume it's a current cluster URL
        return true;
    }
};

const isOrgPerCustomEmbedUrlEnabled = () => {
    return isFeatureEnabled(
        FEATURE_FLAG_NAMES.ORG_PER_CUSTOM_EMBED_URL_FLAG_KEY,
        {
            urlParamValue: envFlags.getValue(
                BLINK_FLAGS.orgPerCustomEmbedUrl?.name,
            ),
        },
    );
};

const getQueryParamsForEmbedUrl = () => {
    const queryParams: QueryParams = {};
    if (isOrgUiEnabled() && isOrgPerCustomEmbedUrlEnabled()) {
        queryParams[
            jsonConstants.ORG_OVERRIDE_QUERY_PARAM_KEY
        ] = getCurrentOrgId().toString();
    }
    return queryParams;
};

export function addEverywhereEventProps(eventProps: {
    [key: string]: any;
}): void {
    const eventProperties = eventProps;
    const EverywhereConstants = blinkConstants.everyWhere;
    const { search, hash } = window?.location;
    const searchParams = new URLSearchParams(search);
    const isFullApp = searchParams.get('isFullAppEmbed') === 'true';
    const hostAppUrl = searchParams.get(EverywhereConstants.hostAppUrl);
    const isEmbedPath = hash.includes('/embed/');
    const isPinboardPath = hash.includes('/viz/');
    const isAnswerPath =
        hash.includes('/answer') || hash.includes('/saved-answer');
    const sdkVersion = searchParams.get(EverywhereConstants.sdkVersion);
    let sdkComponent = null;

    if (isEmbedPath) {
        if (isPinboardPath) {
            const [pinboardId, vizId] = hash
                .split('/viz/')[1]
                .split('?')[0]
                .replace(/\/+$/, '')
                .split('/');
            if (pinboardId) {
                sdkComponent = vizId
                    ? EverywhereConstants.visualisation
                    : EverywhereConstants.pinboard;
            }
        } else if (isAnswerPath) {
            sdkComponent = EverywhereConstants.search;
            eventProperties[
                EverywherePropKeys.visualSDKDataSourceGUIDs
            ] = searchParams.get(EverywhereConstants.dataSources);
        }
    } else if (isFullApp) {
        sdkComponent = EverywhereConstants.fullApp;
    }

    if (sdkVersion) {
        eventProperties[EverywhereConstants.sdkVersion] = sdkVersion;
    }

    eventProperties[EverywherePropKeys.visualSDKComponent] = sdkComponent;
    if (hostAppUrl) {
        eventProperties[EverywherePropKeys.hostAppURL] = hostAppUrl;
    }
    eventProperties[
        EverywherePropKeys.onPublicInstance
    ] = isPublicUserEnabled();
}

export function getEmbeddedCopyUrl(
    objectType: string,
    objectId: string,
    isSpotIQInsight: boolean,
    pinboardVizId: string,
    embedUrls: EmbedUrls,
): string {
    if (_.isNil(embedUrls) || _.isEmpty(embedUrls)) return '';

    let embeddedCopyUrl = '';
    const isSavedAnswer = objectType === ListType.QuestionAnswerBook;
    const isPinboard = objectType === ListType.PinboardAnswerBook;
    if (isSavedAnswer && embedUrls[EmbedUrlsEnum.ANSWER_URL]) {
        embeddedCopyUrl = embedUrls[EmbedUrlsEnum.ANSWER_URL].replaceAll(
            '{object-id}',
            objectId,
        );
    } else if (isPinboard) {
        if (isSpotIQInsight && embedUrls[EmbedUrlsEnum.INSIGHT_URL]) {
            embeddedCopyUrl = embedUrls[EmbedUrlsEnum.INSIGHT_URL].replaceAll(
                '{object-id}',
                objectId,
            );
        } else if (pinboardVizId && embedUrls[EmbedUrlsEnum.PINBOARD_VIZ_URL]) {
            embeddedCopyUrl = embedUrls[EmbedUrlsEnum.PINBOARD_VIZ_URL]
                .replaceAll('{object-id}', objectId)
                .replaceAll('{sub-object-id}', pinboardVizId);
        } else if (embedUrls[EmbedUrlsEnum.PINBOARD_URL]) {
            embeddedCopyUrl = embedUrls[EmbedUrlsEnum.PINBOARD_URL].replaceAll(
                '{object-id}',
                objectId,
            );
        }
    }

    embeddedCopyUrl = addQueryParamsToUrlTemplate(
        embeddedCopyUrl,
        getQueryParamsForEmbedUrl(),
    );
    return cleanupQueryParamsTemplateFromUrl(embeddedCopyUrl);
}

export const notifyAuthInit = (
    userGUID: string,
    mixpanelToken: string,
    isPublicUser: boolean,
) => {
    dispatchNonMemoEvent(EventType.AuthInit, {
        userGUID,
        mixpanelToken,
        isPublicUser,
    });
};

// eslint-disable-next-line security/detect-non-literal-regexp
const PINBOARD_REGEX = new RegExp(
    '#\\/(?:insights\\/)?pinboard\\/(?<fullPath>[^?]+)',
);
const isValidId = (s: string) => /^[a-zA-Z0-9-]+$/.test(s) && /\d/.test(s);

// eslint-disable-next-line security/detect-non-literal-regexp
const ANSWER_REGEX = new RegExp(
    '#\\/(?:insights\\/)?saved-answer\\/(?<fullPath>[^?]+)',
);

// eslint-disable-next-line security/detect-non-literal-regexp
const INSIGHTS_REGEX = new RegExp(
    '#\\/(?:insights\\/)?(?:insight|view)\\/(?<fullPath>[^?]+)',
);

// Shared Spotter conversation links: #/[insights/]conv-assist/s/<shareId>
// eslint-disable-next-line security/detect-non-literal-regexp
const CONVERSATION_REGEX = new RegExp(
    '#\\/(?:insights\\/)?conv-assist\\/s\\/(?<fullPath>[^?]+)',
);

/**
 * Parses pinboard path segments to extract liveboard ID and visualization ID
 * Exported for testing purposes
 */
export const parsePinboardPath = (
    pathSegments: string[],
): { liveboardId: string; vizId: string } | null => {
    // Format: /{liveboardId}
    if (pathSegments.length === 1) {
        return { liveboardId: pathSegments[0], vizId: '' };
    }
    // Format: /{liveboardId}/{vizId}
    if (pathSegments.length === 2) {
        return { liveboardId: pathSegments[0], vizId: pathSegments[1] };
    }
    // Format: /{liveboardId}/tab/{tabId}/{vizId}
    if (
        pathSegments.length === 4 &&
        pathSegments[1] === 'tab' &&
        isValidId(pathSegments[3])
    ) {
        return { liveboardId: pathSegments[0], vizId: pathSegments[3] };
    }
    // Unrecognized format
    return null;
};

const handlePinboardUrl = (url: string, template: string) => {
    const match = url.match(PINBOARD_REGEX);
    if (!match?.groups?.fullPath) return null;

    const pathSegments = match.groups.fullPath.split('/');
    const parsed = parsePinboardPath(pathSegments);

    // If parsing failed, use the full path as objectId
    if (!parsed) {
        return template
            .replace('{object-id}', match.groups.fullPath)
            .replaceAll('{object-id}', '')
            .replaceAll('{sub-object-id}', '');
    }

    return template
        .replaceAll('{object-id}', parsed.liveboardId)
        .replaceAll('{sub-object-id}', parsed.vizId);
};

const handleAnswerUrl = (url: string, template: string) => {
    const match = url.match(ANSWER_REGEX);
    if (!match?.groups?.fullPath) return null;

    if (match.groups.fullPath.includes('/')) {
        return template
            .replace('{object-id}', match.groups.fullPath)
            .replaceAll('{object-id}', '');
    }
    return template.replaceAll('{object-id}', match.groups.fullPath);
};

const handleInsightsUrl = (url: string, template: string) => {
    const match = url.match(INSIGHTS_REGEX);
    if (!match?.groups?.fullPath) return null;

    if (match.groups.fullPath.includes('/')) {
        return template
            .replace('{object-id}', match.groups.fullPath)
            .replaceAll('{object-id}', '');
    }
    return template.replaceAll('{object-id}', match.groups.fullPath);
};

const handleConversationUrl = (url: string, template: string) => {
    const match = url.match(CONVERSATION_REGEX);
    if (!match?.groups?.fullPath) return null;

    if (match.groups.fullPath.includes('/')) {
        return template
            .replace('{conversation-id}', match.groups.fullPath)
            .replaceAll('{conversation-id}', '');
    }
    return template.replaceAll('{conversation-id}', match.groups.fullPath);
};

// We override the navigation URL's also with subscription URL's, as per current pattern.
export function getCustomUrl(url: string, embedUrls: EmbedUrls) {
    if (!isCurrentClusterUrl(url)) {
        return url;
    }

    // Input URLs have params before the hash: ?params#/path
    const existingQueryParams: QueryParams = {};
    const queryBeforeHashMatch = url.match(/\?([^#]+)#/);
    if (queryBeforeHashMatch && queryBeforeHashMatch[1]) {
        const searchParams = new URLSearchParams(queryBeforeHashMatch[1]);
        searchParams.forEach((value, key) => {
            existingQueryParams[key] = value;
        });
    }

    const urlWithoutQueryParams = url.replace(/\?[^#]+/, '');

    let customUrl =
        (embedUrls.PINBOARD_URL &&
            handlePinboardUrl(urlWithoutQueryParams, embedUrls.PINBOARD_URL)) ||
        (embedUrls.ANSWER_URL &&
            handleAnswerUrl(urlWithoutQueryParams, embedUrls.ANSWER_URL)) ||
        (embedUrls.INSIGHT_URL &&
            handleInsightsUrl(urlWithoutQueryParams, embedUrls.INSIGHT_URL)) ||
        (embedUrls.CONVERSATION_URL &&
            handleConversationUrl(
                urlWithoutQueryParams,
                embedUrls.CONVERSATION_URL,
            )) ||
        null;

    if (!customUrl && embedUrls.GENERIC_URL) {
        const hashPath = urlWithoutQueryParams.split('#')[1]?.slice(1) || '';
        customUrl = embedUrls.GENERIC_URL.replaceAll('{path}', hashPath);
    }

    if (!customUrl) {
        return url;
    }

    const newQueryParams = getQueryParamsForEmbedUrl();
    // When linkOverridesV2 is enabled, also preserve existing query params
    const allQueryParams: QueryParams = isLinkOverridesV2Enabled()
        ? { ...existingQueryParams, ...newQueryParams }
        : newQueryParams;

    customUrl = addQueryParamsToUrlTemplate(customUrl, allQueryParams);
    return cleanupQueryParamsTemplateFromUrl(customUrl);
}

export function hasCustomUrls(
    embedUrls: EmbedUrls | null,
): embedUrls is EmbedUrls {
    return !!embedUrls;
}

/**
 * Transforms a ThoughtSpot URL to use custom embed URLs if configured
 *
 * This function replaces internal ThoughtSpot URLs with custom embed URLs
 * based on the organization's embed configuration. It automatically checks
 * the feature flag and fetches embed URLs internally.
 *
 * @param url - The original ThoughtSpot URL (typically a hash path like '#/pinboard/abc-123')
 * @returns The transformed URL, or the original URL if feature is disabled or no custom URLs configured
 *
 * @example
 * ```typescript
 * transformThoughtSpotUrl('#/pinboard/abc-123');
 * // Returns: 'https://custom-domain.com/pinboard/abc-123' (if feature enabled and custom URLs configured)
 * // Returns: '#/pinboard/abc-123' (if feature disabled or no custom URLs)
 * ```
 */
export function transformThoughtSpotUrl(url: string): string {
    if (!url) {
        return url;
    }

    if (!isLinkOverridesV2Enabled()) {
        return url;
    }

    const embedUrls = getCurrentOrgEmbedUrls();
    if (!hasCustomUrls(embedUrls)) {
        return url;
    }

    return getCustomUrl(url, embedUrls);
}

/**
 * Sets up embed initialization with window.open interception
 * This orchestrates the setup of browser interceptors with business logic
 */
export const setupEmbedInit = () => {
    subscribeToCustomCssEvents();

    const getEmbedTarget = (target?: string) => {
        return isDisableRedirectionLinksInNewTabEnabled() ? '_self' : target;
    };

    createWindowOpenInterceptor(transformThoughtSpotUrl, getEmbedTarget);
};

/**
 * Sets embed initialization data and sets up embed interceptors
 * This is the main entry point for embed initialization
 */
export const setEmbedInitData = (data: any) => {
    setEmbedInitDataValues(data);
    setupEmbedInit();
};
